import React, { useState, useEffect } from 'react';
import { Search, Building, Calendar, Trash2, Filter, Download } from 'lucide-react';
import ClientFormatDetails from './ClientFormatDetails';
import UploadSheetWizard from './UploadSheetWizard';
import { getAllClientFormats } from '../../api/createClientApi';
import { deleteClientFormat, downloadClientFormat } from '../../api/clientFormatapi';
import { getExportFileName } from '../../utils/fileExportNaming';
import CustomSelect from '../shared/CustomSelect';
import { toast } from '../shared/Toast';
import { showConfirm } from '../shared/ConfirmContext';

/* ─── Filter Panel for List Page ─────────────────────────────────────── */
const FilterPanel = ({ isOpen, onClose, onApply, clients = [] }) => {
  const [clientName, setClientName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ clientName, startDate, endDate });
  };

  const handleReset = () => {
    setClientName('');
    setStartDate('');
    setEndDate('');
    onApply({ clientName: '', startDate: '', endDate: '' });
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} />
      <div style={{
        position: 'absolute',
        top: '60px',
        right: '20px',
        width: '280px',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        zIndex: 1001,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#20B967' }}>Filters</h2>
          <span style={{ fontSize: '13px', color: '#555' }}>Filter &amp; Sort</span>
        </div>

        {/* Date Range */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '8px', textTransform: 'uppercase' }}>Date Range</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ flex: 1, padding: '8px', border: '1px solid #C5C5C5', borderRadius: '6px', fontSize: '13px' }}
              placeholder="From"
            />
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ flex: 1, padding: '8px', border: '1px solid #C5C5C5', borderRadius: '6px', fontSize: '13px' }}
              placeholder="To"
            />
          </div>
        </div>

        {/* Client Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '8px', textTransform: 'uppercase' }}>Select Client</label>
          <CustomSelect
            value={clientName}
            onChange={(val) => setClientName(val)}
            options={[
              { value: '', label: 'All Clients' },
              ...clients.map((c) => ({ value: c, label: c }))
            ]}
            placeholder="All Clients"
            searchable
            width="100%"
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={handleReset}
            style={{ flex: 1, padding: '10px', border: '1px solid #C5C5C5', borderRadius: '6px', backgroundColor: 'white', color: '#555', fontSize: '14px', cursor: 'pointer' }}
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            style={{ flex: 2, padding: '10px', border: 'none', borderRadius: '6px', backgroundColor: '#20B967', color: 'white', fontSize: '14px', cursor: 'pointer' }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

const TopBar = ({ onAddNew, onSearch, onOpenFilters }) => {
  const [hoverAdd, setHoverAdd] = useState(false);
  const [hoverFilter, setHoverFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <div style={{ backgroundColor: '#EEF8F3', borderRadius: '8px', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#333', margin: 0 }}>Trip Sheets</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F0EEF6', borderRadius: '20px', padding: '10px 16px', width: '240px' }}>
          <input
            type="text"
            placeholder="Company name"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ border: 'none', background: 'transparent', outline: 'none', flexGrow: 1, fontFamily: 'inherit', color: '#333', fontSize: '14px' }}
          />
          <Search size={18} color="#555" style={{ width: 18, height: 18 }} />
        </div>
        <button
          onClick={onOpenFilters}
          onMouseEnter={() => setHoverFilter(true)}
          onMouseLeave={() => setHoverFilter(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: '1px solid #C5C5C5',
            borderRadius: '6px',
            backgroundColor: hoverFilter ? '#f0f9ff' : 'white',
            color: '#555',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          <Filter size={16} />
          Filters
        </button>
        <button
          onMouseEnter={() => setHoverAdd(true)} onMouseLeave={() => setHoverAdd(false)}
          onClick={onAddNew}
          style={{ backgroundColor: hoverAdd ? '#1c9d57' : '#20B967', color: 'white', border: 'none', borderRadius: '6px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, transition: 'background-color 0.2s' }}
        >
          Add New Sheet
        </button>
      </div>
    </div>
  );
};

const TripSheetRow = ({ company, date, formatId, onViewDetails, onDateChange, onDelete, onDownload }) => {
  const [hoverView, setHoverView] = useState(false);
  const [hoverDelete, setHoverDelete] = useState(false);
  const [hoverDownload, setHoverDownload] = useState(false);

  return (
    <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '200px' }}>
          <Building size={18} color="#20B967" />
          <span style={{ fontSize: '15px', color: '#333', fontWeight: 500 }}>{company}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Calendar size={18} color="#20B967" />
          </div>
          <span style={{ fontSize: '15px', color: '#555' }}>{date}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={onViewDetails}
          onMouseEnter={() => setHoverView(true)} onMouseLeave={() => setHoverView(false)}
          style={{ backgroundColor: hoverView ? '#dcf3e6' : '#E5F8ED', color: '#20B967', border: 'none', borderRadius: '20px', padding: '8px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, transition: 'background-color 0.2s' }}
        >
          View Details
        </button>
        <button
          onClick={() => onDownload(formatId, company, date)}
          onMouseEnter={() => setHoverDownload(true)} onMouseLeave={() => setHoverDownload(false)}
          style={{ backgroundColor: hoverDownload ? '#e0f2fe' : '#F0F9FF', color: '#0EA5E9', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
          title="Download"
        >
          <Download size={16} />
        </button>
        <button
          onClick={() => onDelete(formatId)}
          onMouseEnter={() => setHoverDelete(true)} onMouseLeave={() => setHoverDelete(false)}
          style={{ backgroundColor: hoverDelete ? '#fad5d5' : '#FCE8E8', color: '#F04438', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const ClientFormat = () => {
  const [viewingDetails, setViewingDetails] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    clientName: '',
    startDate: '',
    endDate: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });

  useEffect(() => {
    fetchClientFormats();
  }, []);

  const fetchClientFormats = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getAllClientFormats();
      const formats = response.data || response || [];
      const mappedData = formats.map(item => ({
        id: item.id || item.formatId || item.clientFormatId,
        company: item.client?.clientName || item.clientName || 'Unknown Client',
        date: item.createdAt 
          ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
          : new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      }));
      setData(mappedData);
    } catch (err) {
      setError('Failed to load trip sheets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (company, newDate) => {
    const formattedDate = new Date(newDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    setData(prevData => 
      prevData.map(item => 
        item.company === company 
          ? { ...item, date: formattedDate }
          : item
      )
    );
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredData = data.filter(item => {
    // Apply search filter
    const matchesSearch = item.company?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    // Apply client filter
    if (filters.clientName && item.company !== filters.clientName) {
      return false;
    }

    // Apply date range filter (parse the date string)
    if (filters.startDate || filters.endDate) {
      const itemDate = new Date(item.date);
      if (filters.startDate && itemDate < new Date(filters.startDate)) return false;
      if (filters.endDate && itemDate > new Date(filters.endDate)) return false;
    }

    return true;
  }).sort((a, b) => {
    const { sortBy, sortOrder } = filters;
    let valA, valB;

    switch (sortBy) {
      case 'date':
        valA = new Date(a.date);
        valB = new Date(b.date);
        break;
      case 'client':
      case 'company':
        valA = a.company.toLowerCase();
        valB = b.company.toLowerCase();
        break;
      default:
        return 0;
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Extract unique clients for filter dropdown
  const uniqueClients = [...new Set(data.map(item => item.company))];

  const handleViewDetails = (company, date, formatId) => {
    setSelectedDate(date);
    setSelectedFormatId(formatId);
    setViewingDetails(true);
  };

  const handleDownload = async (formatId, companyName, itemDate) => {
    try {
      const response = await downloadClientFormat(formatId);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientNameSafe = (companyName || 'TripSheet').replace(/\s+/g, '_');
      const filename = getExportFileName({
        reportName: `${clientNameSafe}_TripSheet`,
        fromDate: filters.startDate,
        toDate: filters.endDate,
        fallbackDate: itemDate,
        ext: 'xlsx'
      });
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download trip sheet');
    }
  };

  const handleBack = () => {
    setViewingDetails(false);
    setSelectedDate(null);
    setSelectedFormatId(null);
  };

  const handleDelete = async (formatId) => {
    const confirmed = await showConfirm({
      title: 'Delete Client Sheet',
      message: 'Are you sure you want to delete this sheet?',
      danger: true,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await deleteClientFormat(formatId);
      toast.success('Sheet deleted successfully!');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete sheet');
      setError('Failed to delete sheet');
    }
  };

  if (viewingDetails) {
    return <ClientFormatDetails onBack={handleBack} date={selectedDate} formatId={selectedFormatId} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
      <TopBar onAddNew={() => setWizardOpen(true)} onSearch={handleSearch} onOpenFilters={() => setIsFilterOpen(true)} />
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        clients={uniqueClients}
      />
      {searchTerm && (
        <div style={{ padding: '8px 16px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '14px', color: '#0369a1' }}>
          Found {filteredData.length} company{filteredData.length !== 1 ? 's' : ''} matching "{searchTerm}"
        </div>
      )}
      {isLoading && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px', backgroundColor: 'white', borderRadius: '8px' }}>
          Loading trip sheets...
        </div>
      )}
      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '14px' }}>
          {error}
        </div>
      )}
      {!isLoading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <TripSheetRow 
                key={index} 
                company={item.company} 
                date={item.date} 
                formatId={item.id}
                onViewDetails={() => handleViewDetails(item.company, item.date, item.id)} 
                onDateChange={handleDateChange}
                onDelete={handleDelete}
                onDownload={handleDownload}
              />
            ))
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              {searchTerm ? `No company found matching "${searchTerm}"` : 'No trip sheets available'}
            </div>
          )}
        </div>
      )}
      <UploadSheetWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSave={(result) => {
          fetchClientFormats();
        }}
      />
    </div>
  );
};

export default ClientFormat;
